import { useState, type FormEvent } from "react";
import { useReveal } from "@/hooks/useReveal";

const FORM_URL = "https://readdy.ai/api/form/da8vck4tvq5lvgtnasgg";

const EXPORT_OPTIONS = [
  "Yes, to the EU or US",
  "Yes, to other markets",
  "No, domestic only",
];

export default function SpecForm() {
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const headingRef = useReveal<HTMLDivElement>();
  const formRef = useReveal<HTMLDivElement>();

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const hp = (formData.get("company_alt") as string) || "";
    if (hp.trim() !== "") {
      setStatus("success");
      setErrorMsg("");
      form.reset();
      return;
    }

    const body = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== "company_alt" && typeof value === "string" && value.trim() !== "") {
        body.append(key, value);
      }
    });

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText;

      const ok =
        res.ok && (parsed?.code === "OK" || (!parsed && responseText.length > 0));

      if (ok) {
        setStatus("success");
        setErrorMsg("");
        form.reset();
      } else {
        setStatus("error");
        setErrorMsg(
          typeof serverMsg === "string" && serverMsg.length > 0
            ? serverMsg
            : "Something went wrong. Please try again."
        );
      }
    } catch {
      setStatus("error");
      setErrorMsg("Something went wrong. Please try again.");
    }
  };

  return (
    <section id="quote" className="bg-ink py-20 md:py-28">
      <div className="max-w-[1200px] mx-auto px-4 md:px-6">
        <div ref={headingRef} className="reveal text-center">
          <h2 className="text-cream font-serif text-3xl md:text-4xl font-semibold">
            Request the full spec sheet and a sample CoA
          </h2>
          <p className="mt-3 text-cream/70 text-lg">
            Five fields. We will review your request and reply.
          </p>
        </div>

        <div
          ref={formRef}
          className="reveal mt-10 max-w-[640px] mx-auto bg-white rounded-lg p-7 md:p-10"
        >
          <form data-readdy-form onSubmit={handleSubmit} noValidate>
            <input
              type="text"
              name="company_alt"
              className="hp-field"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="email" className="block text-ink text-sm font-semibold mb-1.5">
                  Work email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="you@company.co.za"
                  className="w-full rounded-md border border-cream-dark px-4 py-3 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand/50"
                />
              </div>
              <div>
                <label htmlFor="company" className="block text-ink text-sm font-semibold mb-1.5">
                  Company name
                </label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  required
                  placeholder="Your company"
                  className="w-full rounded-md border border-cream-dark px-4 py-3 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand/50"
                />
              </div>
            </div>

            <div className="mt-5 space-y-5">
              <div>
                <label htmlFor="ingredient" className="block text-ink text-sm font-semibold mb-1.5">
                  Which ingredient?
                </label>
                <select
                  id="ingredient"
                  name="ingredient"
                  required
                  defaultValue=""
                  className="w-full rounded-md border border-cream-dark px-4 py-3 text-sm text-ink bg-white focus:outline-none focus:ring-2 focus:ring-brand/50"
                >
                  <option value="" disabled>
                    Select an option
                  </option>
                  <option>Organic whole cane sugar</option>
                  <option>Organic cocoa powder</option>
                  <option>Both</option>
                  <option>Cocoa butter, nibs or shell</option>
                  <option>Not sure yet</option>
                </select>
              </div>

              <div>
                <label htmlFor="application" className="block text-ink text-sm font-semibold mb-1.5">
                  Application
                </label>
                <select
                  id="application"
                  name="application"
                  required
                  defaultValue=""
                  className="w-full rounded-md border border-cream-dark px-4 py-3 text-sm text-ink bg-white focus:outline-none focus:ring-2 focus:ring-brand/50"
                >
                  <option value="" disabled>
                    Select an application
                  </option>
                  <option>Bakery and confectionery</option>
                  <option>Chocolate</option>
                  <option>Cereal bars and snacks</option>
                  <option>Beverages</option>
                  <option>Dairy and desserts</option>
                  <option>Brewing</option>
                  <option>Infant or specialised food</option>
                  <option>Cosmetics</option>
                  <option>Other</option>
                </select>
              </div>

              <div>
                <label htmlFor="volume" className="block text-ink text-sm font-semibold mb-1.5">
                  Estimated annual volume
                </label>
                <select
                  id="volume"
                  name="volume"
                  required
                  defaultValue=""
                  className="w-full rounded-md border border-cream-dark px-4 py-3 text-sm text-ink bg-white focus:outline-none focus:ring-2 focus:ring-brand/50"
                >
                  <option value="" disabled>
                    Select a range
                  </option>
                  <option>Under 5 tonnes</option>
                  <option>5 to 20 tonnes</option>
                  <option>20 to 60 tonnes</option>
                  <option>60 to 120 tonnes</option>
                  <option>Above 120 tonnes</option>
                </select>
              </div>

              <div>
                <span className="block text-ink text-sm font-semibold mb-2">
                  Do you export finished product with an organic claim?
                </span>
                <div className="space-y-2">
                  {EXPORT_OPTIONS.map((option) => (
                    <label
                      key={option}
                      className="flex items-center gap-3 rounded-md border border-cream-dark px-4 py-3 cursor-pointer hover:bg-cream/50 transition-colors"
                    >
                      <input
                        type="radio"
                        name="export_claim"
                        value={option}
                        required
                        className="accent-brand w-4 h-4"
                      />
                      <span className="text-ink text-sm">{option}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label htmlFor="stage" className="block text-ink text-sm font-semibold mb-1.5">
                  Stage
                </label>
                <select
                  id="stage"
                  name="stage"
                  required
                  defaultValue=""
                  className="w-full rounded-md border border-cream-dark px-4 py-3 text-sm text-ink bg-white focus:outline-none focus:ring-2 focus:ring-brand/50"
                >
                  <option value="" disabled>
                    Select a stage
                  </option>
                  <option>Formulation and trials</option>
                  <option>Supplier homologation</option>
                  <option>Ready to order</option>
                  <option>Benchmarking suppliers</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              className="mt-7 w-full inline-flex items-center justify-center px-6 py-4 rounded-md bg-brand text-white text-base font-semibold hover:bg-brand-dark transition-colors"
            >
              Send request
            </button>

            {status === "success" && (
              <p className="mt-4 text-center text-emerald-700 text-sm font-medium">
                Thank you. We have received your request and will reply.
              </p>
            )}
            {status === "error" && (
              <p className="mt-4 text-center text-red-600 text-sm font-medium">
                {errorMsg}
              </p>
            )}

            <p className="mt-5 text-center text-ink/50 text-xs leading-relaxed">
              We will only use these details to reply to your enquiry. You can
              ask us to delete your data at any time by writing to{" "}
              sara.grisales@mesabaja.com.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}